import { useMemo } from "react";
import type { CSSProperties } from "react";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { transformEmailThemeToBuilderUI } from "@/react/types/EmailTheme";
import type { MarketingEmailTemplate } from "@/react/types/MarketingEmailTemplates";
import { DEFAULT_THEME } from "../../VisualEmailBuilder/Design/defaultTheme";
import { useIsVisualEmailBuilderThemesEnabled } from "../../VisualEmailBuilder/hooks/useIsVisualEmailBuilderThemesEnabled";
import { ContentRenderer } from "./ContentRenderer";
import styles from "./ScaledPreview.module.css";

const EMAIL_CONTENT_WIDTH = 600;
const PREVIEW_WIDTH = 260;
const PREVIEW_HEIGHT = 360;
const FRAME_PADDING = 18;
const FRAME_BACKGROUND_COLOR_TEXT = "#F9FAFB";
const FRAME_BACKGROUND_COLOR_VISUAL = "#EFF6FF";
const FRAME_SHADOW =
  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)";

const PREVIEW_SURFACE_STYLES = {
  backgroundColor: "white",
  width: "100%",
  overflow: "hidden",
  borderRadius: "4px",
} as const;

interface ScaledTemplatePreviewProps {
  containerWidth?: number;
  content?: MarketingEmailTemplate["content"];
  template?: MarketingEmailTemplate;
}

const useScaleCalculation = (containerWidth: number) => {
  const availableWidth = Math.max(containerWidth - FRAME_PADDING * 2, 0);
  const scale = Math.min(availableWidth / EMAIL_CONTENT_WIDTH, 1);
  return scale;
};

export const ScaledTemplatePreview = ({
  template,
  content,
  containerWidth = PREVIEW_WIDTH,
}: ScaledTemplatePreviewProps) => {
  const isThemesEnabled = useIsVisualEmailBuilderThemesEnabled();
  const scale = useScaleCalculation(containerWidth);
  const isVisualTemplate = template?.content_type === "visual_email_builder";

  const theme = useMemo(
    () =>
      isThemesEnabled && template?.email_theme
        ? transformEmailThemeToBuilderUI(template.email_theme)
        : DEFAULT_THEME,
    [isThemesEnabled, template?.email_theme],
  );

  const containerDynamicStyles = useMemo(
    () => ({
      minHeight: `${PREVIEW_HEIGHT}px`,
      padding: `${FRAME_PADDING}px`,
      backgroundColor: isVisualTemplate
        ? FRAME_BACKGROUND_COLOR_VISUAL
        : FRAME_BACKGROUND_COLOR_TEXT,
      boxShadow: FRAME_SHADOW,
    }),
    [isVisualTemplate],
  );

  const containerClasses = classNames(
    "relative size-full overflow-hidden rounded-[inherit] box-border",
    "border border-[#E4E7EB]",
  );

  const contentStyles = useMemo(
    () =>
      ({
        width: `${EMAIL_CONTENT_WIDTH}px`,
        transform: `scale(${scale})`,
        transformOrigin: "top center",
        pointerEvents: "none" as const,
        overflow: "visible",
        scrollbarWidth: "none" as const,
        msOverflowStyle: "none" as const,
        fontSmooth: "always" as const,
        WebkitFontSmoothing: "antialiased" as const,
        MozOsxFontSmoothing: "grayscale" as const,
        position: "relative" as const,
        left: "50%",
        marginLeft: `-${EMAIL_CONTENT_WIDTH / 2}px`,
      }) as CSSProperties,
    [scale],
  );

  return (
    <div className={containerClasses} style={containerDynamicStyles}>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- CSS module imports are typed as any by default */}
      <div className={styles.scrollbarHide} style={contentStyles}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- CSS module imports are typed as any by default */}
        <div className={styles.contentWrapper} style={PREVIEW_SURFACE_STYLES}>
          <ContentRenderer
            template={template}
            content={content}
            theme={theme}
          />
        </div>
      </div>
    </div>
  );
};
